/**
 * TemplatesTab — gallery of company-managed newsletter templates (Firestore
 * emailTemplates where category === 'newsletter' and not deactivated — the
 * CRM Admin "Active" toggle unpublishes a template from this gallery).
 * Two ways to adopt one:
 *
 *   "Stay synced"      — creates a newsletter LINKED to the template; every
 *                        send pulls the template's latest version, so admins
 *                        can refresh content weekly and subscribed LOs send
 *                        it automatically on their own schedule.
 *   "Copy & customize" — snapshots the template into the LO's own editable
 *                        content (the original behavior).
 *
 * Admins manage the templates themselves in CRM Admin → Templates.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  SimpleGrid,
  Text,
  Heading,
  HStack,
  Icon,
  Button,
  Flex,
  Tag,
  Skeleton,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { FiLayout, FiEye, FiCopy, FiRefreshCw } from 'react-icons/fi';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import EmailPreviewModal from '../../../../components/CRM/Email/EmailPreviewModal';

function stripHtmlSnippet(html, maxLen = 140) {
  if (!html) return '';
  const text = html
    .replace(/<style[^>]*>.*?<\/style>/gis, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

export default function TemplatesTab({ onUseTemplate, creating }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const snippetBg = useColorModeValue('gray.50', 'gray.700');

  const [templates, setTemplates] = useState(null);
  const previewModal = useDisclosure();
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db, 'emailTemplates'), where('category', '==', 'newsletter'))
        );
        if (!cancelled) {
          setTemplates(
            snapshot.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              // Admins unpublish via the Active toggle; missing flag = active.
              .filter((t) => t.isActive !== false)
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          );
        }
      } catch (err) {
        console.error('[TemplatesTab] Load failed:', err);
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    previewModal.onOpen();
  };

  if (templates === null) {
    return (
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
        {[0, 1, 2].map((i) => <Skeleton key={i} h="180px" borderRadius="xl" />)}
      </SimpleGrid>
    );
  }

  if (templates.length === 0) {
    return (
      <Flex
        data-tour="mm-templates-grid"
        direction="column"
        align="center"
        justify="center"
        py={16}
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        borderStyle="dashed"
        gap={3}
      >
        <Icon as={FiLayout} boxSize={10} color="blue.300" />
        <Heading size="sm">No company templates yet</Heading>
        <Text fontSize="sm" color={mutedColor} maxW="440px" textAlign="center">
          CRM admins can publish ready-to-send newsletter designs under CRM Admin → Templates by
          setting the template category to <b>newsletter</b>. They'll show up here for every loan
          officer to adopt.
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <Text fontSize="sm" color={mutedColor} mb={4} data-tour="mm-templates-note">
        <b>Stay synced</b> always sends the latest version of a template — when admins refresh
        it (say, every Sunday), your newsletter automatically sends the new content on your
        schedule. <b>Copy &amp; customize</b> takes a one-time snapshot you can edit freely.
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4} data-tour="mm-templates-grid">
        {templates.map((template) => (
          <Box
            key={template.id}
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            shadow="sm"
            p={5}
            transition="all 0.2s"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            display="flex"
            flexDirection="column"
          >
            <HStack spacing={2} mb={2}>
              <Tag size="sm" colorScheme="blue" variant="subtle">Company template</Tag>
            </HStack>
            <Heading size="sm" noOfLines={1}>{template.name || 'Untitled template'}</Heading>
            <Text fontSize="xs" color={mutedColor} noOfLines={1} mt={1}>
              {template.subject || '(no subject)'}
            </Text>
            <Box bg={snippetBg} borderRadius="md" p={3} mt={3} flex={1}>
              <Text fontSize="xs" color={mutedColor} noOfLines={3}>
                {stripHtmlSnippet(template.body) || 'No content'}
              </Text>
            </Box>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<FiRefreshCw />}
              mt={4}
              isLoading={creating}
              onClick={() => onUseTemplate({
                name: template.name || 'Untitled Newsletter',
                contentSource: 'template',
                linkedTemplateId: template.id,
              })}
            >
              Stay synced — always send the latest version
            </Button>
            <HStack mt={2} spacing={2}>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiCopy />}
                flex={1}
                isLoading={creating}
                onClick={() => onUseTemplate({
                  name: template.name || 'Untitled Newsletter',
                  subject: template.subject || '',
                  bodyHtml: template.body || '',
                  sourceTemplateId: template.id,
                })}
              >
                Copy &amp; customize
              </Button>
              <Button size="sm" variant="outline" leftIcon={<FiEye />} onClick={() => handlePreview(template)}>
                Preview
              </Button>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>

      <EmailPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => { previewModal.onClose(); setPreviewTemplate(null); }}
        body={previewTemplate?.body || ''}
        subject={previewTemplate?.subject || '(no subject)'}
        toLabel="to your realtors"
      />
    </>
  );
}
